import React, { useState, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcChartLine, FaIcon } from "../../Icons";
import axios from "axios";
import Header from "../../layout/Header/Header";
import { Input, Label, Spinner } from "reactstrap";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList,
} from "recharts";

// ── Helpers ───────────────────────────────────────────────────────────────────
const C1    = "#3b82f6";  // año principal (azul)
const C2    = "#cbd5e1";  // año comparado (gris claro)
const fmt   = (v) => "$" + new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v || 0);
const fmtK  = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return fmt(n);
};
const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#0f172a", borderRadius: 10, padding: "10px 16px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.25)", fontSize: "0.78rem",
    }}>
      <div style={{ color: "#94a3b8", fontWeight: 700, marginBottom: 6, fontSize: "0.72rem" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.fill, display: "inline-block" }} />
            <span style={{ color: "#94a3b8", fontSize: "0.7rem" }}>{p.name}</span>
          </div>
          <span style={{ color: "#fff", fontWeight: 800 }}>{fmt(p.value)}</span>
        </div>
      ))}
      {payload.length === 2 && (() => {
        const diff = (payload.find(p => p.dataKey === "actual")?.value || 0) - (payload.find(p => p.dataKey === "anterior")?.value || 0);
        const isPos = diff >= 0;
        return (
          <div style={{ borderTop: "1px solid #1e293b", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", gap: 16 }}>
            <span style={{ color: "#64748b", fontSize: "0.7rem" }}>Diferencia</span>
            <span style={{ color: isPos ? "#34d399" : "#f87171", fontWeight: 800 }}>
              {isPos ? "+" : ""}{fmt(diff)}
            </span>
          </div>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function GraficaVentasComparativa() {
  const hoy        = new Date();
  const anioActual = hoy.getFullYear();

  const { user }     = useContext(AuthContext);
  const URL_GRAFICAS = process.env.REACT_APP_URL_GRAFICAS;

  const anios = Array.from({ length: anioActual - 2020 + 1 }, (_, i) => anioActual - i);

  const [graficas, setGraficas] = useState([]);
  const [anio1,    setAnio1]    = useState(anioActual);
  const [anio2,    setAnio2]    = useState(anioActual - 1);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    const mesesVisibles = anio1 === anioActual ? hoy.getMonth() + 1 : 12;
    const finAnio1      = anio1 === anioActual
      ? `${anio1}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${new Date(anio1, hoy.getMonth() + 1, 0).getDate()}`
      : `${anio1}-12-31`;

    Promise.all([
      axios.get(`${URL_GRAFICAS}/ventaMensual/${anio1}-01-01/${finAnio1}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      }),
      axios.get(`${URL_GRAFICAS}/ventaMensual/${anio2}-01-01/${anio2}-12-31`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      }),
    ])
      .then(([r1, r2]) => {
        const d1 = r1.data || [];
        const d2 = r2.data || [];
        const combined = MONTHS.slice(0, mesesVisibles).map((mes, idx) => ({
          Mes:      mes,
          actual:   Number(d1[idx]?.Importe) || 0,
          anterior: Number(d2[idx]?.Importe) || 0,
        }));
        setGraficas(combined);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [anio1, anio2]); // eslint-disable-line

  const stats = useMemo(() => {
    if (!graficas.length) return null;
    const totalActual   = graficas.reduce((s, g) => s + g.actual,   0);
    const totalAnterior = graficas.reduce((s, g) => s + g.anterior, 0);
    const diff          = totalActual - totalAnterior;
    const pct           = totalAnterior > 0 ? (diff / totalAnterior * 100) : 0;
    const mejorMesIdx   = graficas.reduce((best, g, i) => g.actual > graficas[best].actual ? i : best, 0);
    return {
      totalActual, totalAnterior, diff, pct: pct.toFixed(1), isPos: pct >= 0,
      mejorMes: graficas[mejorMesIdx]?.Mes || "",
      mejorVal: graficas[mejorMesIdx]?.actual || 0,
    };
  }, [graficas]);

  if (!user.usuarios_permisos) return null;

  return (
    <>
      <Header />
      <div className="card container col-sm-11" style={{ paddingBottom: 32 }}>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <IcChartLine style={{ marginRight: 8, color: C1 }} />
              Venta Comparativa
            </h3>
            <p className="page-subtitle">Ingresos mensuales: año actual vs año anterior</p>
          </div>
          <div className="page-actions">
            <a href="/MenuReportes" style={{ fontSize: "0.78rem", color: "#64748b", textDecoration: "none" }}>
              ← Reportes
            </a>
          </div>
        </div>

        {/* ── Filter bar ───────────────────────────────────────────────── */}
        <div className="filter-bar">
          <div>
            <Label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" }}>AÑO PRINCIPAL</Label>
            <Input bsSize="sm" type="select" value={anio1} onChange={(e) => setAnio1(Number(e.target.value))} style={{ minWidth: 110 }}>
              {anios.map((a) => <option key={a} value={a}>{a}</option>)}
            </Input>
          </div>
          <div>
            <Label style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" }}>AÑO COMPARAR</Label>
            <Input bsSize="sm" type="select" value={anio2} onChange={(e) => setAnio2(Number(e.target.value))} style={{ minWidth: 110 }}>
              {anios.map((a) => <option key={a} value={a}>{a}</option>)}
            </Input>
          </div>
          {loading && <Spinner size="sm" style={{ color: C1, alignSelf: "flex-end", marginBottom: 4 }} />}
        </div>

        {/* ── KPI cards ────────────────────────────────────────────────── */}
        {stats && (
          <div className="row mb-3">
            {[
              { label: `Total ${anio1}`,   value: fmtK(stats.totalActual),   sub: "año principal",    icon: "fas fa-chart-bar",   color: C1,        bg: "#eff6ff" },
              { label: `Total ${anio2}`,   value: fmtK(stats.totalAnterior), sub: "año comparado",    icon: "fas fa-history",     color: "#64748b", bg: "#f8fafc" },
              { label: "Diferencia",       value: `${stats.isPos ? "+" : ""}${fmtK(Math.abs(stats.diff))}`, sub: stats.isPos ? "por encima" : "por debajo",
                icon: stats.isPos ? "fas fa-arrow-up" : "fas fa-arrow-down",
                color: stats.isPos ? "#10b981" : "#ef4444",
                bg:    stats.isPos ? "#f0fdf4" : "#fff1f2" },
              { label: "Variación",        value: `${stats.isPos ? "+" : ""}${stats.pct}%`, sub: `vs ${anio2}`,
                icon: "fas fa-percent",
                color: stats.isPos ? "#10b981" : "#ef4444",
                bg:    stats.isPos ? "#f0fdf4" : "#fff1f2" },
            ].map((card, i) => (
              <div key={i} className="col-6 col-md-3 mb-2">
                <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderTop: `3px solid ${card.color}`,
                  display: "flex", alignItems: "center", gap: 14, height: "100%" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: card.bg,
                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaIcon cls={card.icon} size={18} style={{ color: card.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{card.value}</div>
                    <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#64748b",
                      textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{card.label}</div>
                    <div style={{ fontSize: "0.72rem", color: card.color, fontWeight: 600, marginTop: 2 }}>{card.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Chart ────────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
            <Spinner style={{ color: C1, width: "2.5rem", height: "2.5rem" }} />
            <div style={{ marginTop: 14, fontWeight: 600 }}>Cargando datos…</div>
          </div>
        ) : graficas.length > 0 ? (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
            padding: "20px 12px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={graficas} margin={{ top: 16, right: 16, left: 8, bottom: 0 }} barGap={3}>
                <defs>
                  <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C1} stopOpacity={0.9} />
                    <stop offset="95%" stopColor={C1} stopOpacity={0.55} />
                  </linearGradient>
                  <linearGradient id="gradAnterior" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#94a3b8" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#94a3b8" stopOpacity={0.35} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="Mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={60} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "#f8fafc" }} />
                <Legend
                  formatter={(v) => v === "actual" ? String(anio1) : String(anio2)}
                  wrapperStyle={{ fontSize: "0.75rem", paddingTop: 12, color: "#475569" }}
                />
                <Bar dataKey="anterior" name="anterior" fill="url(#gradAnterior)" radius={[4, 4, 0, 0]} maxBarSize={32} />
                <Bar dataKey="actual"   name="actual"   fill="url(#gradActual)"   radius={[4, 4, 0, 0]} maxBarSize={32}>
                  <LabelList dataKey="actual" position="top"
                    style={{ fontSize: "0.6rem", fontWeight: 700, fill: C1 }}
                    formatter={(v) => v > 0 ? fmtK(v) : ""} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <IcChartLine style={{ fontSize: "3rem", opacity: 0.2 }} />
            <div style={{ marginTop: 12, fontWeight: 600 }}>Sin datos en el período</div>
          </div>
        )}

      </div>
    </>
  );
}

export default GraficaVentasComparativa;
